/**
 * A call recording, played inside the page.
 *
 * This was a bare `<audio controls>`. It worked, but every browser draws that widget
 * differently and none of them draw it like the rest of this app — a grey slab with a
 * three-dot menu offering "download" and "playback speed" in the middle of a call
 * history nobody asked to be a media library. It also gave no sense of the clip's shape
 * and no way to jump into the middle of a two-minute call without dragging a hairline.
 *
 * So: our own transport over a hidden `<audio>` element. Same source, same
 * `preload="none"` — a row with ten calls still fetches nothing until somebody presses
 * play — but the control belongs to the page, and clicking anywhere on the waveform
 * jumps there.
 */
import React, { useMemo, useRef, useState } from 'react';
import { FaPlay, FaPause, FaRedo, FaMicrophone } from 'react-icons/fa';

const mmss = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

/**
 * A waveform we did not measure.
 *
 * The real envelope would mean decoding the file, which would mean downloading it — the
 * one thing `preload="none"` exists to avoid. These bars are derived from the URL
 * instead: meaningless as data, but stable per recording, so a call always looks like
 * itself and the bar under the cursor does not move while you aim at it.
 */
const barsFor = (seed, count) => {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const out = [];
  for (let i = 0; i < count; i += 1) {
    h ^= h << 13; h ^= h >>> 17; h ^= h << 5; h |= 0;
    out.push(0.3 + ((Math.abs(h) % 1000) / 1000) * 0.7);
  }
  return out;
};

export default function CallRecording({ url, name, durationSeconds, compact = false }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(durationSeconds || 0);
  const [failed, setFailed] = useState(false);

  const count = compact ? 26 : 44;
  const bars = useMemo(() => barsFor(url || 'recording', count), [url, count]);

  if (!url) return null;

  const finished = dur > 0 && pos >= dur - 0.25;

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); return; }
    if (finished) a.currentTime = 0;
    // A rejected play() is the usual autoplay-policy or decode failure; say so quietly.
    Promise.resolve(a.play()).catch(() => setFailed(true));
  };

  const seekTo = (fraction) => {
    const a = ref.current;
    if (!a || !Number.isFinite(a.duration) || a.duration <= 0) return;
    a.currentTime = Math.min(a.duration, Math.max(0, fraction * a.duration));
    setPos(a.currentTime);
  };

  const onWaveClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seekTo((e.clientX - rect.left) / rect.width);
  };

  /* Arrow keys nudge by five seconds — reviewing a call means re-hearing one sentence. */
  const onWaveKey = (e) => {
    const a = ref.current;
    if (!a) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      if (!Number.isFinite(a.duration)) return;
      a.currentTime = Math.min(a.duration, Math.max(0, a.currentTime + (e.key === 'ArrowRight' ? 5 : -5)));
      setPos(a.currentTime);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      toggle();
    }
  };

  const progress = dur > 0 ? Math.min(1, pos / dur) : 0;
  const played = Math.round(progress * count);

  return (
    <div className={`scc-rec${compact ? ' compact' : ''}`}>
      <audio
        ref={ref}
        src={url}
        preload="none"
        onPlay={() => { setPlaying(true); setFailed(false); }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.currentTarget.duration)) setDur(e.currentTarget.duration);
        }}
        onError={() => setFailed(true)}
      />

      <button
        type="button"
        className={`scc-rec-btn${!playing && !finished ? ' is-play' : ''}`}
        onClick={toggle}
        title={playing ? 'Pause' : 'Play the recording'}
        aria-label={playing ? 'Pause the recording' : 'Play the recording'}
      >
        {playing ? <FaPause size={compact ? 9 : 11} />
          : finished ? <FaRedo size={compact ? 9 : 11} />
            : <FaPlay size={compact ? 9 : 11} />}
      </button>

      <div
        className="scc-rec-wave"
        onClick={onWaveClick}
        onKeyDown={onWaveKey}
        role="slider"
        tabIndex={0}
        aria-label="Seek within the recording"
        aria-valuemin={0}
        aria-valuemax={Math.round(dur)}
        aria-valuenow={Math.round(pos)}
        aria-valuetext={`${mmss(pos)} of ${mmss(dur)}`}
        title={name || 'Call recording'}
      >
        {bars.map((b, i) => (
          <span
            key={i}
            className={i < played ? 'on' : ''}
            style={{ height: `${Math.max(12, b * 100)}%` }}
          />
        ))}
      </div>

      <span className="scc-rec-time">
        {failed ? 'unavailable' : dur > 0 ? `${mmss(pos)} / ${mmss(dur)}` : '· · ·'}
      </span>

      {!compact && <FaMicrophone className="scc-rec-mic" size={10} title={name || 'Call recording'} />}
    </div>
  );
}
